<panel printable width="840">
	<menu always-visible>
		<menuitem (click)="togglePrintMode()" [icon]="printMode ? 'print-slash' : 'print-magnifying-glass'">{{ (printMode ? 'company-invoices.cancel-export-print-mode' : 'company-invoices.show-export-print-mode') | translate }}</menuitem>
		<menuitem *requiredCredential="credentialType.EditLegalCases" (click)="openCreateDebtCollection()" icon="hand-holding-dollar">{{ 'company-invoices.create-debt-collection' | translate }}</menuitem>
	</menu>
	<h1 hide-on-print>{{ 'company-invoices.header' | translate }}</h1>
	@if (!printMode) {
		<section>
			@if (!loading) {
				<div class="align-right"><h3>{{ 'company-invoices.summary-excl-vat' | translate }}: {{ totalLedgerEntryValues | currency }}</h3></div>
				<div class="align-right"><h3>{{ 'company-invoices.summary-incl-vat' | translate }}: {{ totalLedgerEntryValuesInclVat | currency }}</h3></div>
			}
			@if (loading) {
				<div class="align-right"><skeleton /></div>
				<div class="align-right"><skeleton /></div>
			}
		</section>
	}

	@if (printMode) {
		<section hide-on-print three-columns>
			<div><date-input [label]="'company-invoices.input.from-date' | translate" [(value)]="fromDate" (valueChange)="filterLedgerEntries()" [minDate]="oldestDate" [maxDate]="toDate ?? newestDate" /></div>
			<div><date-input [label]="'company-invoices.input.to-date' | translate" [(value)]="toDate" (valueChange)="filterLedgerEntries()" [minDate]="fromDate ?? oldestDate" [maxDate]="today" /></div>
			<button (click)="print()" [loader]="false">{{ 'company-invoices.print' | translate }}</button>
		</section>
	}
	@if (printMode) {
		<section>
			<text-group>
				<span>{{ 'company-invoices.print-date' | translate }}</span>
				<span>{{ today | customDate: 'shortDate' }}</span>
				<span>{{ 'company-invoices.print.from-date' | translate }}</span>
				<span>{{ (fromDate ? fromDate : oldestDate) | customDate: 'shortDate' }}</span>
				<span>{{ 'company-invoices.print.to-date' | translate }}</span>
				<span>{{ (toDate ? toDate : today) | customDate: 'shortDate' }}</span>
			</text-group>
		</section>
	}
	@if (printMode && billingInfo) {
		<section>
			{{ billingInfo.companyName }}<br />
			{{ billingInfo.streetAddress }}<br />
			{{ billingInfo.zipcode }} {{ billingInfo.city }}
		</section>
	}

	@if (!loading) {
		<list-view #entriesList class="scrollable" [emptyLabel]="error ? 'company-invoices.error' : 'company-invoices.ledger-list-empty-label'">
			<header [sortColumn]="!printMode" [filterColumn]="!printMode">{{ 'company-invoices.column.ledger-posted-no' | translate }}</header>
			<header [sortColumn]="!printMode" [filterColumn]="!printMode">{{ 'company-invoices.column.ledger-type' | translate }}</header>
			@if (!printMode) {
				<header [sortColumn]="!printMode" [filterColumn]="!printMode">{{ 'company-invoices.column.ledger-remark' | translate }}</header>
			}
			@if (printMode) {
				<header [sortColumn]="!printMode" [filterColumn]="!printMode">{{ 'company-invoices.column.ledger-description' | translate }}</header>
			}
			<header [sortColumn]="!printMode" [filterColumn]="!printMode" [defaultSort]="printMode ? 'ascending' : 'descending'">{{ 'company-invoices.column.ledger-postingDate' | translate }}</header>
			<header [sortColumn]="!printMode" [filterColumn]="!printMode">{{ 'company-invoices.column.ledger-dueDate' | translate }}</header>
			<header [sortColumn]="!printMode" [filterColumn]="!printMode" align-right>{{ 'company-invoices.column.ledger-value' | translate }}</header>
			<header [sortColumn]="!printMode" [filterColumn]="!printMode" align-right>{{ (printMode ? 'company-invoices.column.ledger-remaining-value.print' : 'company-invoices.column.ledger-remaining-value') | translate }}</header>
			@if (!printMode) {
				<header [sortColumn]="!printMode" [filterColumn]="!printMode" align-right>{{ 'company-invoices.column.ledger-closed' | translate }}</header>
			}
			@if (printMode) {
				<header [sortColumn]="!printMode" [filterColumn]="!printMode" align-right>{{ 'company-invoices.column.cumulative-total' | translate }}</header>
			}
			@if (printMode && previousTotalLedgerEntryValues !== undefined) {
				<row>
					<column />
					<column>{{ 'company-invoices.previous-total' | translate }}</column>
					<column />
					<column />
					<column />
					<column />
					<column />
					<column />
					<column align-right>{{ previousTotalLedgerEntryValues | currency }}</column>
				</row>
			}
			<row *listData="let ledgerEntry from filteredLedgerEntries"
				 (action)="selectedCustomerLedgerEntry = ledgerEntry"
				 [brand]="ledgerEntry.brandId"
				 [clickable]="detailedViewAvailable(ledgerEntry)"
				 [highlighted]="selectedCustomerLedgerEntry == ledgerEntry"
				 [showCollapseIcon]="true"
				 [collapsed]="(!printMode && installmentPayments(ledgerEntry)) ? !showPaymentsList(ledgerEntry) : undefined"
				 (collapsedChange)="showPaymentsList(ledgerEntry, !$event)"
				 [statusColor]="entryStatusColor(ledgerEntry)"
				 [statusTitle]="('ledger-document.status.' + entryStatusTitle(ledgerEntry)) | translate">
				<column>{{ ledgerEntry.documentNo }}</column>
				<column>
					{{ ('company-invoices.bc-document-type.' + bcDocumentType[ledgerEntry.documentType]) | translate }}
					@if (!printMode && installmentPayments(ledgerEntry)) {
						({{ 'company-invoices.installment-payments.number-of-installments' | translate:{ installments: installmentPayments(ledgerEntry)?.length } }})
					}
				</column>
				@if (!printMode) {
					<column [ngClass]="{ attentionClass: requiresAttention(ledgerEntry) }">{{ getLedgerRemarks(ledgerEntry.documentNo) }}</column>
				}
				@if (printMode) {
					<column>{{ ledgerEntry.description }}</column>
				}
				<column [sortValue]="ledgerEntry.postingDate">{{ ledgerEntry.postingDate | customDate: 'shortDate' }}</column>
				<column [sortValue]="ledgerEntry.dueDate">{{ ledgerEntry.dueDate | customDate: 'shortDate' }}</column>
				<column align-right>{{ ledgerEntry.amountInclVat | currency}}</column>
				<column align-right>{{ ledgerEntry.remainingAmountInclVat | currency}}</column>
				@if (!printMode) {
					<column align-right>{{ (ledgerEntry.open ? 'general.false' : 'general.true') | translate }}</column>
				}
				@if (printMode) {
					<column align-right>{{ getCumulativeTotal(ledgerEntry) | currency}}</column>
				}

				@if (!printMode && showPaymentsList(ledgerEntry)) {
					<list-view [parentList]="entriesList">
						<row *listData="let payment from installmentPayments(ledgerEntry) ?? []; index as i" [clickable]="false" [statusColor]="paymentStatusColor(payment)">
							<column>{{ 'company-invoices.installment-payments.payment-x-of-y' | translate: { index: i+1, total: installmentPayments(ledgerEntry)?.length } }}</column>
							<column />
							<column />
							<column />
							<column>{{ payment.dueDate | customDate: 'shortDate' }}</column>
							<column />
							<column align-right>{{ payment.amount | currency }}</column>
							<column align-right>{{ (payment.paid ? 'general.true' : 'general.false') | translate }}</column>
						</row>
					</list-view>
				}
			</row>
		</list-view>
	}
	@if (printMode) {
		<h3 align-right>{{ 'company-invoices.print.summary' | translate }}: {{ totalFilteredLedgerEntryValuesInclVat | currency }}</h3>
	}
	@if (loading) {
		<skeletons [defaultSkeletons]="5" />
	}
</panel>

@if (selectedCustomerLedgerEntry) {
	<ledger-entry-details #entryDetails
						  [documentId]="getDocumentId(selectedCustomerLedgerEntry)"
						  [farpayInvoiceId]="getFarpayInvoiceId(selectedCustomerLedgerEntry)"
						  [objection]="invoiceObjection(selectedCustomerLedgerEntry)"
						  [issuedReminder]="issuedReminder(selectedCustomerLedgerEntry)"
						  [installmentPayments]="installmentPayments(selectedCustomerLedgerEntry)"
						  [ledgerEntry]="selectedCustomerLedgerEntry"
						  [companyId]="companyId" />
}

@if (company && billingInfo) {
	<panel width="420">
		<menu always-visible>
			<menuitem *requiredCredential="credentialType.UpdateCompanyFromCvr" (click)="editBillingInfo()" icon="building">{{ 'company-invoices.edit-billing-info.context-menu' | translate }}</menuitem>
		</menu>
		<h1>{{ 'company-invoices.billing-information' | translate }}</h1>
		<company-information [company]="company" [billingInfo]="billingInfo" />
		<list-contact [company]="company" [showJustDefaultContactForBilling]="true" />
	</panel>
}
